import type { CSSProperties, ReactNode } from "react";

import styles from "./one-system.module.css";
import { appSurfaceStyle } from "./product-bridge/app-surface";
import { APP_THEME_TOKENS } from "./product-bridge/app-theme-tokens";

/*
 * One system: a marketing page in the application's own design system, so
 * the page and its product shots read as one. The app's light tokens
 * verbatim, with the marketing tokens re-pointed at them, so every shared
 * section (the nav, the bands, the grids) resolves to the product's colours
 * too, and the site's square edge. `claret` wears the app's editorial
 * theme, on the page and inside every shot; `blue` keeps the app's default.
 *
 * The type is a separate choice. `app` takes the application's as well:
 * Inter at 450, its leading and its text scale. `site` keeps the site's own
 * faces and scale, which is what the site's shell draws every page in
 * (Archie, 2026-10-01: the previous fonts); the shots inside keep the app's
 * type either way, as the app draws them.
 */

export type OneSystemTheme = "blue" | "claret";
export type OneSystemType = "app" | "site";

/** The marketing tokens, re-pointed at the application's. */
const SITE_TOKENS_AS_APP = {
  backgroundColor: "hsl(var(--background))",
  color: "hsl(var(--foreground))",
  "--theme-ink": "hsl(var(--foreground))",
  "--theme-ground": "hsl(var(--background))",
  "--theme-accent-text": "hsl(var(--primary))",
  /* The site's accent on a dark surface: a pale tint of the primary. */
  "--theme-accent": "color-mix(in srgb, hsl(var(--primary)) 22%, white)",
  "--theme-dark": "hsl(var(--solid))",
  "--theme-surface-dark": "hsl(var(--solid))",
  "--rule": "hsl(var(--border))",
  "--rule-soft": "hsl(var(--border))",
} as CSSProperties;

/** The style a One system page's root wears, beside oneSystemClass. */
export function oneSystemStyle(type: OneSystemType = "app"): CSSProperties {
  if (type === "site") return { ...APP_THEME_TOKENS.light, colorScheme: "light", ...SITE_TOKENS_AS_APP } as CSSProperties;
  return { ...appSurfaceStyle("light"), ...SITE_TOKENS_AS_APP, "--font-figtree": "var(--font-inter)" } as CSSProperties;
}

/** Square corners alone, for a page that keeps the editorial look. */
export const SQUARE_CLS = styles.square;

/** Inter's display tightening, for the headings larger than the app sets. */
export const ONE_SYSTEM_DISPLAY_CLS = styles.display;

/** The classes a One system page's root wears, beside oneSystemStyle. */
export function oneSystemClass(theme: OneSystemTheme = "claret", type: OneSystemType = "app"): string {
  return `${styles.page} ${styles.square} ${type === "app" ? styles.appType : ""} ${theme === "claret" ? styles.claret : ""}`;
}

/** A subtree in One system, for a page whose chrome is not the site's shell. */
export function OneSystem({ theme = "claret", type = "app", children }: { theme?: OneSystemTheme; type?: OneSystemType; children: ReactNode }) {
  return (
    <div className={oneSystemClass(theme, type)} style={oneSystemStyle(type)}>
      {children}
    </div>
  );
}
